import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import {
  ArrowUpRight, Building2, GraduationCap, Users, Shield,
  Smartphone, FileText, CheckCircle, Send, Globe, Award,
  Heart, MessageSquare, Briefcase, ArrowRight,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, EditorialHeader, Section, Heading, EBtn } from "@/styles/editorial";

const SERVICES = [
  {
    num: "01", icon: Building2, title: "Planes Municipales", color: C.blue,
    desc: "Diseño de planes de emergencia animal adaptados a tu municipio. Análisis de riesgos, protocolos de actuación y coordinación con servicios de emergencia.",
    audience: "Ayuntamientos",
  },
  {
    num: "02", icon: Shield, title: "Protección Civil", color: C.red,
    desc: "Formación especializada para equipos de protección civil en rescate y manejo de animales durante emergencias y catástrofes.",
    audience: "Administración Pública",
  },
  {
    num: "03", icon: Heart, title: "Formación Protectoras", color: C.orange,
    desc: "Capacitación para refugios y protectoras: planes de evacuación, protocolos de emergencia y coordinación con voluntarios.",
    audience: "ONGs y Protectoras",
  },
  {
    num: "04", icon: Users, title: "Formación Ciudadanía", color: C.lilac,
    desc: "Talleres y cursos para la ciudadanía sobre preparación ante emergencias con animales de compañía.",
    audience: "Ciudadanos",
  },
  {
    num: "05", icon: FileText, title: "Planes Individualizados", color: C.olive,
    desc: "Planes de emergencia personalizados según tu ubicación, tipo de animales y riesgos específicos de tu zona.",
    audience: "Particulares, Empresas y Entidades",
  },
  {
    num: "06", icon: Briefcase, title: "Consultoría Empresas", color: C.navy,
    desc: "Asesoramiento para empresas del sector animal: clínicas veterinarias, tiendas, residencias caninas, granjas...",
    audience: "Sector Privado",
  },
];

const FORMATIONS = [
  { title: "Vínculo Humano-Animal en Emergencias", hours: "8h", mode: "Presencial / Online", color: C.red },
  { title: "Primeros Auxilios para Animales", hours: "12h", mode: "Presencial", color: C.blue },
  { title: "Evacuación y Rescate Animal", hours: "16h", mode: "Presencial", color: C.orange },
  { title: "Gestión de Refugios en Crisis", hours: "8h", mode: "Online", color: C.lilac },
];

export default function ConsultoriaPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "", email: "", organization: "", org_type: "", service_interest: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Por favor, completa los campos obligatorios");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/consultoria/contact`, formData);
      setSent(true);
      toast.success("¡Solicitud enviada! Te contactaremos pronto.");
    } catch (err) {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: C.white }} data-testid="consultoria-page">
      <SEO
        title="Consultoría"
        description="Servicios profesionales de consultoría en protección animal para municipios, protectoras y organizaciones. Planes de emergencia, formación y protocolos."
        path="/consultoria"
      />
      <EditorialHeader backLabel="Volver al Hub" backTo="/" />

      {/* ─── HERO ─── */}
      <section className="px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-32" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase"
                  style={{ fontFamily: MONO, background: C.red, color: C.white, borderRadius: 2 }}>
                  MAKING CITIES RESILIENT
                </span>
                <span className="px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase"
                  style={{ fontFamily: MONO, background: C.blue, color: C.white, borderRadius: 2 }}>
                  MCR2030
                </span>
              </div>
              <h1
                className="text-[clamp(2.5rem,6vw,5rem)] leading-[1.02] tracking-tight mb-6"
                style={{ fontFamily: DISPLAY, color: C.white }}
              >
                Consultoría en<br />
                <span style={{ color: C.orange }}>emergencias</span>{" "}
                <span style={{ color: C.white }}>animales</span>
              </h1>
              <p className="text-lg md:text-xl max-w-2xl mb-10" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.8)", lineHeight: 1.7 }}>
                Ayudamos a municipios, protectoras y organizaciones a estar preparados
                para proteger a los animales en cualquier emergencia.
                Alineados con los objetivos de resiliencia de la ONU.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#servicios">
                  <EBtn bg={C.orange} color={C.navy} testId="cta-servicios">
                    Ver servicios <ArrowUpRight className="w-4 h-4" />
                  </EBtn>
                </a>
                <a href="#contacto">
                  <EBtn outline bg={C.white} testId="cta-contactar">
                    Contactar <Send className="w-4 h-4" />
                  </EBtn>
                </a>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-4 hidden md:block">
            <Reveal delay={0.2}>
              <div className="flex flex-col items-end gap-2 text-right">
                {[
                  { v: "15+", l: "Años experiencia" },
                  { v: "50+", l: "Formaciones" },
                  { v: "30+", l: "Municipios" },
                  { v: "1000+", l: "Profesionales formados" },
                ].map((s, i) => (
                  <div key={i} className="flex items-baseline gap-3">
                    <span className="text-xs tracking-widest uppercase" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.45)" }}>{s.l}</span>
                    <span className="text-2xl font-bold" style={{ fontFamily: DISPLAY, color: C.orange }}>{s.v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <Section bg={C.white} id="servicios" testId="consultoria-services">
        <EditorialLabel number="01" text="SERVICIOS" color={C.olive} />
        <Heading size="lg" className="mb-4">¿Qué ofrecemos?</Heading>
        <p className="text-base md:text-lg max-w-2xl mb-12" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>
          Servicios de consultoría y formación adaptados a las necesidades de cada organización.
        </p>

        <div className="space-y-0">
          {SERVICES.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div
                className="grid md:grid-cols-12 gap-6 py-8 items-start"
                style={{ borderBottom: `1px solid ${C.navy}10` }}
                data-testid={`service-${i}`}
              >
                <div className="md:col-span-1 flex items-center gap-3">
                  <span className="text-xs font-semibold tracking-widest" style={{ fontFamily: MONO, color: s.color }}>{s.num}</span>
                </div>
                <div className="md:col-span-3 flex items-center gap-3">
                  <s.icon className="w-5 h-5 flex-shrink-0" style={{ color: s.color }} />
                  <h3 className="text-lg font-bold" style={{ fontFamily: DISPLAY, color: C.navy }}>{s.title}</h3>
                </div>
                <div className="md:col-span-6">
                  <p className="text-sm md:text-base" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>{s.desc}</p>
                </div>
                <div className="md:col-span-2 flex md:justify-end">
                  <span className="text-[10px] font-semibold tracking-[0.15em] uppercase px-3 py-1"
                    style={{ fontFamily: MONO, color: s.color, border: `1px solid ${s.color}30`, borderRadius: 2 }}>
                    {s.audience}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ─── APP PERSONALIZADA ─── */}
      <section className="px-6 md:px-12 py-16 md:py-24" style={{ background: C.navy }} id="app">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <EditorialLabel number="02" text="PRODUCTO ESTRELLA" color={C.orange} />
              <Heading size="lg" className="mb-6">
                <span style={{ color: C.white }}>App </span>
                <span style={{ color: C.orange }}>personalizada</span>
              </Heading>
              <p className="text-base md:text-lg mb-8" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.75)", lineHeight: 1.7 }}>
                Basada en nuestro ecosistema Emergencia Animal, creamos aplicaciones
                a medida para tu organización: coordinación interna, mensajería segura,
                alertas geolocalizadas y gestión de emergencias.
              </p>
              <ul className="space-y-4 mb-10">
                {[
                  "Análisis de riesgos según geolocalización",
                  "Cálculo de eventos más probables en tu zona",
                  "Planes generados según tipos de animales",
                  "Base de datos cruzada inteligente",
                  "Branding personalizado con tu imagen",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.85)" }}>
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: C.orange }} />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <a href="#contacto">
                <EBtn bg={C.orange} color={C.navy} testId="cta-app">
                  Solicitar información <Smartphone className="w-4 h-4" />
                </EBtn>
              </a>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: MessageSquare, t: "Mensajería interna" },
                  { icon: Shield, t: "Alertas push" },
                  { icon: Globe, t: "Geolocalización" },
                  { icon: Award, t: "Dashboard analytics" },
                ].map((f, i) => (
                  <div key={i} className="py-5 px-4" style={{ background: "rgba(255,255,255,0.06)", borderLeft: `2px solid ${C.orange}40`, borderRadius: 2 }}>
                    <f.icon className="w-5 h-5 mb-2" style={{ color: C.orange }} />
                    <p className="text-sm font-semibold" style={{ fontFamily: BODY, color: C.white }}>{f.t}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── FORMACIONES ─── */}
      <Section bg={C.white} testId="consultoria-formations">
        <EditorialLabel number="03" text="FORMACIÓN" color={C.blue} />
        <Heading size="lg" className="mb-4">Cursos y talleres</Heading>
        <p className="text-base md:text-lg max-w-2xl mb-12" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>
          Formación especializada impartida por profesionales con experiencia real en emergencias.
        </p>

        <div className="space-y-0">
          {FORMATIONS.map((course, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div
                className="flex items-center gap-6 py-6"
                style={{ borderBottom: `1px solid ${C.navy}10` }}
              >
                <span className="w-1 h-8 flex-shrink-0" style={{ background: course.color, borderRadius: 1 }} />
                <h3 className="flex-1 text-base md:text-lg font-bold" style={{ fontFamily: DISPLAY, color: C.navy }}>{course.title}</h3>
                <span className="text-xs tracking-widest" style={{ fontFamily: MONO, color: C.gray }}>{course.hours}</span>
                <span className="text-xs tracking-widest hidden sm:block" style={{ fontFamily: MONO, color: C.gray }}>{course.mode}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 text-center">
            <p className="text-xs tracking-widest mb-4" style={{ fontFamily: MONO, color: C.gray }}>
              PRECIOS Y FECHAS BAJO DEMANDA
            </p>
            <a href="#contacto">
              <EBtn bg={C.blue} color={C.white} testId="cta-formacion">
                Solicitar información <Send className="w-4 h-4" />
              </EBtn>
            </a>
          </div>
        </Reveal>
      </Section>

      {/* ─── TESTIMONIO ─── */}
      <section className="px-6 md:px-12 py-16 md:py-24" style={{ background: C.red }}>
        <div className="max-w-[900px] mx-auto">
          <Reveal>
            <EditorialLabel text="TESTIMONIO" color="rgba(255,255,255,0.6)" />
            <blockquote
              className="text-xl md:text-2xl lg:text-3xl leading-snug mb-6"
              style={{ fontFamily: DISPLAY, color: C.white }}
            >
              "La formación sobre el vínculo humano-animal en emergencias nos abrió los ojos.
              Ahora entendemos por qué las personas no evacuan sin sus animales y cómo gestionarlo."
            </blockquote>
            <p className="text-xs tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.65)" }}>
              — Participante Curso Protección Civil
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── FORMULARIO ─── */}
      <Section bg={C.white} id="contacto" testId="consultoria-contact">
        <div className="max-w-[720px]">
          <EditorialLabel number="04" text="CONTACTO" color={C.navy} />
          <Heading size="lg" className="mb-3">
            Solicita <span style={{ color: C.orange }}>presupuesto</span>
          </Heading>
          <p className="text-base mb-10" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>
            Cuéntanos qué necesitas y te prepararemos una propuesta personalizada.
          </p>

          {sent ? (
            <Reveal>
              <div className="py-12 text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center" style={{ background: C.olive, borderRadius: 4 }}>
                  <CheckCircle className="w-8 h-8" style={{ color: C.white }} />
                </div>
                <h3 className="text-xl mb-2" style={{ fontFamily: DISPLAY, color: C.navy }}>¡Solicitud enviada!</h3>
                <p className="text-sm mb-6" style={{ fontFamily: BODY, color: C.gray }}>
                  Hemos recibido tu mensaje. Te contactaremos en las próximas 24-48 horas.
                </p>
                <EBtn onClick={() => setSent(false)} bg={C.navy} color={C.white}>Enviar otra consulta</EBtn>
              </div>
            </Reveal>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="consultoria-form">
              <div className="grid md:grid-cols-2 gap-6">
                <FormField label="Nombre *" value={formData.name} onChange={(v) => setFormData({ ...formData, name: v })} placeholder="Tu nombre" required />
                <FormField label="Email *" value={formData.email} onChange={(v) => setFormData({ ...formData, email: v })} type="email" placeholder="tu@email.com" required />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <FormField label="Organización" value={formData.organization} onChange={(v) => setFormData({ ...formData, organization: v })} placeholder="Nombre de tu organización" />
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ fontFamily: MONO, color: C.navy }}>
                    Tipo de organización
                  </label>
                  <select
                    value={formData.org_type}
                    onChange={(e) => setFormData({ ...formData, org_type: e.target.value })}
                    className="w-full px-4 py-3 text-sm outline-none"
                    style={{ fontFamily: BODY, border: `1px solid ${C.navy}20`, borderRadius: 4, background: C.white, color: C.navy }}
                    data-testid="org-type-select"
                  >
                    <option value="">Selecciona...</option>
                    <option value="ayuntamiento">Ayuntamiento</option>
                    <option value="proteccion_civil">Protección Civil</option>
                    <option value="protectora">Protectora / ONG</option>
                    <option value="empresa">Empresa</option>
                    <option value="particular">Particular</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ fontFamily: MONO, color: C.navy }}>
                  Servicio de interés
                </label>
                <select
                  value={formData.service_interest}
                  onChange={(e) => setFormData({ ...formData, service_interest: e.target.value })}
                  className="w-full px-4 py-3 text-sm outline-none"
                  style={{ fontFamily: BODY, border: `1px solid ${C.navy}20`, borderRadius: 4, background: C.white, color: C.navy }}
                  data-testid="service-select"
                >
                  <option value="">Selecciona...</option>
                  <option value="plan_municipal">Plan de Emergencia Municipal</option>
                  <option value="formacion_pc">Formación Protección Civil</option>
                  <option value="formacion_protectoras">Formación Protectoras</option>
                  <option value="formacion_ciudadania">Formación Ciudadanía</option>
                  <option value="plan_individual">Plan Individualizado</option>
                  <option value="app_personalizada">App Personalizada</option>
                  <option value="consultoria_empresa">Consultoría Empresa</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ fontFamily: MONO, color: C.navy }}>
                  Mensaje *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 text-sm min-h-[140px] outline-none"
                  style={{ fontFamily: BODY, border: `1px solid ${C.navy}20`, borderRadius: 4, color: C.navy }}
                  placeholder="Cuéntanos qué necesitas, cuántas personas, fechas preferidas..."
                  required
                  data-testid="message-textarea"
                />
              </div>
              <EBtn type="submit" disabled={sending} bg={C.orange} color={C.navy} testId="submit-btn" className="w-full justify-center">
                {sending ? "Enviando..." : "Enviar solicitud"} <Send className="w-4 h-4" />
              </EBtn>
            </form>
          )}
        </div>
      </Section>

      {/* ─── FOOTER CTA ─── */}
      <section className="px-6 md:px-12 py-16" style={{ background: C.navy }}>
        <div className="max-w-[900px] mx-auto text-center">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO, color: C.orange }}>
              Emergencia Animal
            </p>
            <Heading size="md">
              <span style={{ color: C.white }}>Juntos podemos hacer que ningún animal sea olvidado en una emergencia</span>
            </Heading>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <EBtn onClick={() => navigate("/")} bg={C.white} color={C.navy} testId="back-hub-btn">
                Volver al Hub
              </EBtn>
              <a href="#contacto">
                <EBtn bg={C.orange} color={C.navy} testId="footer-contactar-btn">
                  Contactar <ArrowRight className="w-4 h-4" />
                </EBtn>
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

/* ─── FORM FIELD HELPER ─── */
function FormField({ label, value, onChange, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ fontFamily: MONO, color: C.navy }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 text-sm outline-none"
        style={{ fontFamily: BODY, border: `1px solid ${C.navy}20`, borderRadius: 4, color: C.navy }}
        placeholder={placeholder}
        required={required}
        data-testid={`field-${label.replace(/[^a-z]/gi, "").toLowerCase()}`}
      />
    </div>
  );
}
